import { api } from "../api";

/**
 * Service for fetching images from the backend API
 */
export const imageService = {
    /**
     * Get all images from a specific directory
     * @param {string} directory - The directory path relative to /static
     * @returns {Promise<Array>} - Array of file objects
     */
    getImages: async (directory = "") => {
        try {
            const response = await api.get("/cms/files", {
                params: { directory },
            });

            // Filter out directories and return only image files
            return response.data.files.filter(
                (file) => !file.is_directory && isImageFile(file.filename)
            );
        } catch (error) {
            console.error("Error fetching images:", error);
            throw error;
        }
    },

    /**
     * Get all images from all subdirectories recursively
     * @param {string} rootDirectory - The root directory to start from
     * @returns {Promise<Array>} - Array of file objects
     */
    getAllImagesRecursive: async (rootDirectory = "") => {
        try {
            // Start with the root directory
            const images = [];
            const directories = [rootDirectory];
            const processedDirectories = new Set();

            // Process each directory and its subdirectories
            while (directories.length > 0) {
                const currentDir = directories.shift();

                // Skip if already processed to avoid infinite loops
                if (processedDirectories.has(currentDir)) {
                    continue;
                }

                processedDirectories.add(currentDir);

                // Get files and subdirectories
                const response = await api.get("/cms/files", {
                    params: { directory: currentDir },
                });

                // Add image files to the result
                const files = response.data.files;
                for (const file of files) {
                    if (!file.is_directory && isImageFile(file.filename)) {
                        images.push(file);
                    } else if (file.is_directory && file.filename !== "..") {
                        // Add subdirectories to the queue
                        directories.push(file.path);
                    }
                }
            }

            return images;
        } catch (error) {
            console.error("Error fetching all images:", error);
            throw error;
        }
    },

    /**
     * Get the full URL for an image
     * @param {string} path - The image path relative to /static
     * @returns {string} - The full URL
     */
    getImageUrl: (path) => {
        return `/static/${path}`;
    },

    /**
     * Get gallery images for a property from the database
     * @param {number} propertyId - The property ID
     * @returns {Promise<Array>} - Array of gallery image objects
     */
    getGalleryImages: async (propertyId) => {
        try {
            const response = await api.get(`/gallery/${propertyId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching gallery images:", error);
            throw error;
        }
    },

    /**
     * Upload an image to a property gallery
     * @param {File} file - The image file to upload
     * @param {number} propertyId - The property ID
     * @param {string} cityPath - The city path
     * @param {string} description - Optional description for the image
     * @returns {Promise<Object>} - The uploaded gallery image data
     */
    uploadGalleryImage: async (file, propertyId, cityPath, description = null) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("city_path", cityPath);
            
            if (description) {
                formData.append("description", description);
            }
            
            const response = await api.post(`/gallery/${propertyId}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            
            return response.data;
        } catch (error) {
            console.error("Error uploading gallery image:", error);
            throw error;
        }
    },

    /**
     * Delete a gallery image
     * @param {number} imageId - The gallery image ID
     * @returns {Promise<void>}
     */
    deleteGalleryImage: async (imageId) => {
        try {
            await api.delete(`/gallery/${imageId}`);
        } catch (error) {
            console.error("Error deleting gallery image:", error);
            throw error;
        }
    },

    /**
     * Update a gallery image's metadata
     * @param {number} imageId - The gallery image ID
     * @param {Object} data - The data to update (position, description)
     * @returns {Promise<Object>} - The updated gallery image data
     */
    updateGalleryImage: async (imageId, data) => {
        try {
            const formData = new FormData();
            
            if (data.position !== undefined) {
                formData.append("position", data.position);
            }
            
            if (data.description !== undefined) {
                formData.append("description", data.description);
            }
            
            const response = await api.put(`/gallery/${imageId}`, formData);
            return response.data;
        } catch (error) {
            console.error("Error updating gallery image:", error);
            throw error;
        }
    }
};

/**
 * Check if a filename is an image file
 * @param {string} filename - The filename to check
 * @returns {boolean} - True if the file is an image
 */
function isImageFile(filename) {
    const imageExtensions = [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".bmp",
        ".webp",
        ".svg",
    ];
    const lowerFilename = filename.toLowerCase();
    return imageExtensions.some((ext) => lowerFilename.endsWith(ext));
}
